import { AsyncPipe } from '@angular/common';
import { Component, signal, DOCUMENT, inject, DestroyRef, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { ENV, Env, MarketingLinks, marketingLinks } from '@bodyanalytics/app-config';
import { NavComponent } from '../nav/nav.component';
import { MemberButtonComponent } from '../member-button/member-button.component';
import { AdminMemberButtonComponent } from '../admin-member-button/admin-button.component';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { DarkModeToggleComponent } from '../../theme/dark-mode-toggle.component';
import { ThemeService } from '../../theme/theme.service';
import { BrandLogoComponent } from '../../shared-components/brand-logo/brand-logo.component';

@Component({
  selector: 'bodyanalytics-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    NavComponent,
    MemberButtonComponent,
    AdminMemberButtonComponent,
    ProfileMenuComponent,
    DarkModeToggleComponent,
    BrandLogoComponent,
  ],
})
export class HeaderComponent {
  auth = inject(AuthService);
  private document = inject<Document>(DOCUMENT);
  protected readonly appConfig = inject<Env>(ENV);
  protected readonly theme = inject(ThemeService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  /** Environment-aware marketing destinations (dev vs prod). */
  protected readonly marketing: MarketingLinks = marketingLinks(this.appConfig);

  /**
   * Explicit portal opt-in for apps whose `portalName` is an env-substituted id
   * (e.g. operator's `$PORTAL_ID_OPERATOR`) and therefore can't be matched by the
   * string branches above. The operator app sets `<bodyanalytics-header portal="operator" />`
   * so its branch renders WITHOUT touching the other three apps' env config. (#1658)
   */
  readonly portal = input<'operator' | undefined>(undefined);

  /**
   * Mobile nav (hamburger) open/closed state. The bar collapses the inline nav
   * behind a toggle below ~768px (see header.component.scss). Kept as a signal so
   * the OnPush-friendly template reflects state changes without manual CD. (#1305)
   */
  readonly menuOpen = signal(false);

  constructor() {
    // Auto-close the mobile menu on any successful navigation so the panel never
    // lingers open over a new route. takeUntilDestroyed ties the subscription to
    // the component lifetime so it is torn down on destroy (#1637) — the header
    // sits in every app shell, so a leaked router.events subscription would
    // accumulate across re-creations.
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.closeMenu());
  }

  public toggleMenu(): void {
    this.menuOpen.update(open => !open);
  }

  public closeMenu(): void {
    this.menuOpen.set(false);
  }

  public isHomePage(): boolean {
    return this.router.url === '/home' || this.router.url === '/';
  }

  /**
   * Login / account-creation flow pages (/login, /register/*). On these the
   * visitor is mid-onboarding — they may already be Auth0-authenticated (e.g.
   * customerIdentityGuard sends a signed-in user with no Customer row to
   * /register/account, #840) yet is NOT an onboarded member. The customer-portal
   * authenticated chrome (Dashboard/Calendar/Account tabs + profile menu) is
   * therefore suppressed here, so "create an account" never shows member-only
   * nav. Matches on prefix to cover /register/account and any future children.
   */
  public isAuthFlowPage(): boolean {
    const url = this.router.url;
    return url.startsWith('/login') || url.startsWith('/register');
  }

  public isCustomerPortal(): boolean {
    return this.appConfig.portalName === 'customer';
  }

  public isCalendarPortal(): boolean {
    return this.appConfig.portalName === 'calendar';
  }

  public isAdminPortal(): boolean {
    return this.appConfig.portalName === 'admin';
  }

  /**
   * Operator portal branch. Driven by the explicit `portal` input (NOT
   * `portalName`, which is an env-substituted id in the operator build), so it
   * only renders when the operator app opts in. (#1658)
   */
  public isOperatorPortal(): boolean {
    return this.portal() === 'operator';
  }
}
